import { NewsClipping } from '@/utils/supabase-queries';

// Define excluded categories that will be filtered out by default
// export const excludedCategories = ['unmentioned', 'n/a', 'na', 'N/A', 'uncategorized']; // REMOVED

/**
 * Extract unique values for filter options from news clippings
 */
export const extractFilterOptions = (clippings: NewsClipping[]) => { // Removed showExcludedCategories flag
  const options: Record<string, string[]> = {
    brandName: [], // Keep only this one for brand filtering
    source: [],
    category: [],
    sentiment: [],
    visibility: [] // Add visibility filter option
  };
  
  // Simplified logic: Extract all unique values found
  clippings.forEach(clip => {
    // Access both camelCase and database column names using type assertion
    const anyClip = clip as any;
    
    // Skip ID extraction - we don't want to show IDs in the filter UI
    
    // Handle both "Brand name" (DB) and brandName (camelCase)
    const brandValue = anyClip["Brand name"] || clip.brandName;
    if (brandValue && !options.brandName.includes(brandValue)) {
      options.brandName.push(brandValue);
    }
    
    // Handle both "Source" (DB) and source (camelCase)
    const sourceValue = anyClip.Source || clip.source;
    if (sourceValue && !options.source.includes(sourceValue)) {
      options.source.push(sourceValue);
    }
    
    // Handle both "Category" (DB) and category (camelCase)
    const categoryValue = anyClip.Category || clip.category;
    if (categoryValue && categoryValue !== '' && !options.category.includes(categoryValue)) {
      options.category.push(categoryValue);
    }
    
    // Handle both "Sentiment" (DB) and sentiment (camelCase)
    const sentimentValue = anyClip.Sentiment || clip.sentiment;
    if (sentimentValue && sentimentValue !== '' && !options.sentiment.includes(sentimentValue)) {
      options.sentiment.push(sentimentValue);
    }
    
    // Handle both "Visibility" (DB) and visibility (camelCase)
    const visibilityValue = anyClip.Visibility || clip.visibility;
    if (visibilityValue && !options.visibility.includes(visibilityValue)) {
      options.visibility.push(visibilityValue);
    }
  });
  
  // Sort all arrays and filter out "Unmentioned"
  Object.keys(options).forEach(key => {
    options[key] = options[key]
      .filter(value => value !== "Unmentioned") // Remove any "Unmentioned" values
      .sort((a, b) => String(a).localeCompare(String(b))); // Ensure comparison as strings
  });
  
  return options;
};

export const applyFilters = (
  newsClippings: NewsClipping[], 
  searchTerm: string,
  filters: Record<string, string[]>
  // Removed showExcludedCategories flag
) => {
  
  return newsClippings.filter(clip => {
    // Cast to any to access both camelCase and DB column names
    const anyClip = clip as any;
    
    // Apply search term filter across all relevant fields
    const matchesSearch = searchTerm ? (
       (clip.id && String(clip.id).toLowerCase().includes(searchTerm.toLowerCase())) || // Search ID
      (clip.title || anyClip.Title) && (clip.title || anyClip.Title).toLowerCase().includes(searchTerm.toLowerCase()) || 
      (clip.source || anyClip.Source) && (clip.source || anyClip.Source).toLowerCase().includes(searchTerm.toLowerCase()) || 
      (clip.brandName || anyClip["Brand name"]) && (clip.brandName || anyClip["Brand name"]).toLowerCase().includes(searchTerm.toLowerCase()) || 
      (clip.category || anyClip.Category) && (clip.category || anyClip.Category).toLowerCase().includes(searchTerm.toLowerCase()) || 
      (clip.sentiment || anyClip.Sentiment) && (clip.sentiment || anyClip.Sentiment).toLowerCase().includes(searchTerm.toLowerCase()) || 
      (clip.content || anyClip.artilce_text) && (clip.content || anyClip.artilce_text).toLowerCase().includes(searchTerm.toLowerCase()) || 
      (clip.summary || anyClip.Summary_ENG) && (clip.summary || anyClip.Summary_ENG).toLowerCase().includes(searchTerm.toLowerCase()) ||
      (clip.url || anyClip.URL) && (clip.url || anyClip.URL).toLowerCase().includes(searchTerm.toLowerCase())
    ) : true;

    // Check date range filter FIRST if both dates are present
    let matchesDateRange = true;
    if (filters.startDate?.[0] && filters.endDate?.[0]) {
      const clipDate = clip.date || anyClip.Date;
      if (!clipDate) {
        matchesDateRange = false; // Clip must have a date to match a range
      } else {
        try {
          const dateObj = new Date(clipDate);
          const startDate = new Date(filters.startDate[0]);
          const endDate = new Date(filters.endDate[0]);

          // Ensure dates are valid before comparison
          if (!isNaN(dateObj.getTime()) && !isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
            // Set time to ensure full day comparison
            startDate.setUTCHours(0, 0, 0, 0); // Use UTC to avoid timezone issues if dates are stored as YYYY-MM-DD
            endDate.setUTCHours(23, 59, 59, 999); // Use UTC
            matchesDateRange = dateObj >= startDate && dateObj <= endDate;
          } else {
            matchesDateRange = false; // Invalid date found
          }
        } catch (e) {
          matchesDateRange = false; // Error parsing date
        }
      }
    }

    // Apply all OTHER field filters with multi-select support
    const matchesOtherFilters = Object.entries(filters).every(([field, values]) => {
      // Skip date fields as they are handled above
      if (field === 'startDate' || field === 'endDate') return true;
      
      if (!values || values.length === 0) return true;
      
      // Handle ID filter specifically (ensure string comparison)
      if (field === 'ids') {
        return values.includes(String(clip.id));
      }
      
      // Handle brand filter - map either 'brand' or 'brandName' to the actual brandName property
      if (field === 'brand' || field === 'brandName') {
        const brandValue = clip.brandName || anyClip["Brand name"];
        if (brandValue === null || brandValue === undefined) return false;
        return values.includes(String(brandValue));
      }
      
      // Handle other regular field filters, try both camelCase and DB column names
      let clipValue;
      
      // Map UI filter names to potential database/model property names
      switch(field) {
        case 'source':
          clipValue = clip.source || anyClip.Source;
          break;
        case 'category':
          clipValue = clip.category || anyClip.Category;
          break;
        case 'sentiment':
          clipValue = clip.sentiment || anyClip.Sentiment;
          break;
        case 'visibility':
          clipValue = clip.visibility || anyClip.Visibility;
          break;
        default:
          clipValue = (clip as any)[field]; // Fallback to direct property access
      }
      
      if (clipValue === null || clipValue === undefined) return false; // Clip doesn't have this field set
      
      // Ensure comparison is done correctly (e.g., checking if clipValue is in the array)
      return values.includes(String(clipValue)); // Convert clipValue to string for includes check
    });

    return matchesSearch && matchesDateRange && matchesOtherFilters;
  });
};

// Moved applyAllFiltersToItem function before calculateCrossFilterCounts
function applyAllFiltersToItem(item: any, filters: Record<string, string[]>): boolean {
  // Date range filter (if present in filters object)
  if (filters.startDate?.[0] && filters.endDate?.[0]) {
    const itemDateValue = item.Date || item.date; // Handle both casings
    if (!itemDateValue) return false; // Item must have a date to match a range
    try {
      const itemDate = new Date(itemDateValue);
      const startDate = new Date(filters.startDate[0]);
      const endDate = new Date(filters.endDate[0]);
      
      // Basic date validation
      if (isNaN(itemDate.getTime()) || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return false;
      }

      // Normalize times for comparison (optional, depends on desired precision)
      startDate.setUTCHours(0, 0, 0, 0);
      endDate.setUTCHours(23, 59, 59, 999);

      if (itemDate < startDate || itemDate > endDate) {
        return false;
      }
    } catch (e) {
      return false; // Error parsing date means it doesn't match
    }
  }

  // Other filters
  for (const key in filters) {
    if (key === 'startDate' || key === 'endDate') continue; // Already handled

    const filterValues = filters[key];
    if (!filterValues || filterValues.length === 0) continue;

    let itemValue = getItemValueForCategory(item, key);
    if (itemValue === null || itemValue === undefined) return false; // Item doesn't have value for this filter
    
    // Ensure itemValue is a string for includes check if filterValues are strings
    itemValue = String(itemValue);

    if (!filterValues.includes(itemValue)) {
      return false;
    }
  }
  return true;
}

/**
 * Calculate option counts for faceted filtering.
 * When filters are applied in one category, we want the counts in other categories 
 * to show the breakdown of the filtered results.
 */
export const calculateCrossFilterCounts = (
  allData: any[],
  currentFilters: Record<string, string[]> = {},
): Record<string, Record<string, number>> => {
  // Initialize counts structure for each category
  const counts: Record<string, Record<string, number>> = {
    brand: {},
    sentiment: {},
    category: {},
    source: {},
    visibility: {}
  };

  // Safety check for empty or invalid data
  if (!allData || !Array.isArray(allData) || allData.length === 0) {
    console.warn('[calculateCrossFilterCounts] No data provided or empty array');
    return counts; // Return empty counts structure
  }

  // --- Step 1: Calculate the base filtered items (matching all current filters) ---
  const baseFilteredItems = allData.filter(item => applyAllFiltersToItem(item, currentFilters));
  const baseFilteredCount = baseFilteredItems.length;
  console.log(`[FacetedCounts] Base count (all filters applied): ${baseFilteredCount}`);

  // First populate all possible values from the full dataset
  const categories = ['brand', 'sentiment', 'category', 'source', 'visibility'];
  const allValues: Record<string, Set<string>> = {
    brand: new Set<string>(),
    sentiment: new Set<string>(),
    category: new Set<string>(),
    source: new Set<string>(),
    visibility: new Set<string>()
  };

  // Collect all unique values across the entire dataset
  allData.forEach(item => {
    categories.forEach(category => {
      const value = getItemValueForCategory(item, category);
      if (value) {
        allValues[category].add(value);
      }
    });
  });

  // --- Step 2: For each category, count items in the filtered set ---
  categories.forEach(category => {
    const filterKey = category === 'brand' ? 'brandName' : category;
    
    // Initialize all possible values with zero counts
    allValues[category].forEach(value => {
      counts[category][value] = 0;
    });
    
    // For each category, we need to count items WITH the category's own filters removed
    // This ensures we see what would happen if we toggled each option
    const categoryFilteredItems = [...allData]; // Start with all items
    
    // Count items for each value
    baseFilteredItems.forEach(item => {
      const value = getItemValueForCategory(item, category);
      if (value) {
        counts[category][value] = (counts[category][value] || 0) + 1;
      }
    });

    console.log(`[FacetedCounts] Category [${category}]: ${Object.keys(counts[category]).length} options with counts`);
  });

  return counts;
};

/**
 * Helper function to get the value of an item for a specific category,
 * handling different potential property names.
 */
function getItemValueForCategory(item: any, category: string): string | null {
  if (!item) {
    console.warn(`[getItemValueForCategory] Item is null or undefined for category ${category}`);
    return null;
  }

  const anyItem = item as any;
  let value: any;

  try {
    switch (category) {
      case 'brand':
        value = anyItem.brandName || anyItem['Brand name'];
        break;
      case 'sentiment':
        value = anyItem.sentiment || anyItem.Sentiment;
        break;
      case 'category':
        value = anyItem.category || anyItem.Category;
        break;
      case 'source':
        value = anyItem.source || anyItem.Source;
        break;
      case 'visibility':
        value = anyItem.visibility || anyItem.Visibility;
        break;
      default:
        value = anyItem[category]; // Fallback
    }

    // Return the value as a string, or null if empty/null/undefined
    return (value !== undefined && value !== null && value !== '') ? String(value) : null;
  } catch (error) {
    console.error(`[getItemValueForCategory] Error getting value for ${category}:`, error);
    return null;
  }
}